import { CommonModule, DatePipe } from '@angular/common';
import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';

import { AirlineService, GhaService, RateCategoryEnum } from '@cai-services';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { Subject, takeUntil } from 'rxjs';
import { FakeData, PresignedURL, RateGroup, UFN_DATE } from '../../core';
import {
  displayAircraftTypes,
  displayRateType,
  formatMoneyToString,
  renderAirlineProdCode,
  tipsAirlineProdCode,
  ULDTypeRateList,
  WeightTypeRateList,
} from '../../helpers';
import { AirlineContactPopupComponent } from '../airline-contact-popup/airline-contact-popup.component';
import { AirlineGeneralInformationPopupComponent } from '../airline-general-information-popup/airline-general-information-popup.component';
import { AirlineSchedulePopupComponent } from '../airline-schedule-popup/airline-schedule-popup.component';
import { GhaContactPopupComponent } from '../gha-contact-popup/gha-contact-popup.component';

@Component({
  selector: 'common-group-table',
  imports: [CommonModule, MatTableModule, MatTooltipModule],
  templateUrl: './group-table.component.html',
  styleUrl: './group-table.component.scss',
})
export class GroupTableComponent implements OnInit, OnChanges, OnDestroy {
  @Input() groupedRates: RateGroup[] = [];
  @Input() group: 'airline' | 'commodity';
  @Input() rateCategory: RateCategoryEnum;
  @Input() selectedOfficeId: number;
  isAllInRateEnabled: boolean;
  @Input() showBuyingRate: boolean;
  @Input() shipmentResult: { [key: string]: string };
  @Input() oldDynamicDisplayedColumns: string[];

  private destroy$ = new Subject<void>();
  ufnDate: Date = UFN_DATE;
  currentSortDir = 'asc';
  currentSort: string[];
  displayedColumnsAirline = [
    'airlineProdCode',
    'commodity',
    'rateType',
    'currency',
    'M',
  ];

  displayedColumnsCommodity = [
    'airline',
    'airlineProdCode',
    'rateType',
    'currency',
    'M',
  ];
  dynamicDisplayedColumns = [];

  weightDisplayedColumns = [
    'N',
    'kg45',
    'kg100',
    'kg300',
    'kg500',
    'kg1000',
    'kg3000',
    'fuelSurcharge',
    'securitySurcharge',
    'miscellaneousCharge',
    'saf',
  ];
  flightDisplayedColumns = ['flightNo', 'aircraftTypes', 'shcs', 'density'];
  rateDisplayedColumns = ['startDate', 'endDate', 'day', 'lastUpdatedDate'];
  supportDisplayedColumns = [
    'GHA Contact',
    'Airline Contact',
    'Airline Schedule',
    'T&Cs',
  ];
  tabSelected: string[] = [];
  displayedColumns: string[] = [];

  constructor(
    private readonly datePipe: DatePipe,
    private readonly modalService: NgbModal,
    private readonly ghaService: GhaService,
    private readonly airlineService: AirlineService,
  ) {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateDisplayedColumns() {
    this.displayedColumns =
      this.group === 'airline'
        ? this.displayedColumnsAirline
        : this.displayedColumnsCommodity;
  }
  updateDynamicDisplayedColumns() {
    this.dynamicDisplayedColumns = this.tabSelected.map((tab) => {
      switch (tab) {
        case 'weight':
          return this.shipmentResult
            ? this.oldDynamicDisplayedColumns
            : this.weightDisplayedColumns;
        case 'flight':
          return this.flightDisplayedColumns;
        case 'rate':
          return this.rateDisplayedColumns;
        case 'support':
          return this.supportDisplayedColumns;
        default:
          return [];
      }
    });
  }
  updateWeightDisplayedColumns() {
    const excludedColumns = [
      'fuelSurcharge',
      'securitySurcharge',
      'miscellaneousCharge',
    ];
    if (this.isAllInRateEnabled) {
      this.weightDisplayedColumns = this.weightDisplayedColumns.filter(
        (col) => !excludedColumns.includes(col),
      );
    }
  }
  onTabChange(index: number, newTab: string) {
    this.tabSelected[index] = newTab;
    this.updateDynamicDisplayedColumns();
    this.updateWeightDisplayedColumns();
    console.log(this.weightDisplayedColumns, 'oldDynamicDisplayedColumns');
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.hasOwnProperty('group')) {
      this.updateDisplayedColumns();
    }
    if (changes.hasOwnProperty('groupedRates')) {
      if (this.groupedRates && this.groupedRates.length > 0) {
        this.tabSelected = Array(this.groupedRates.length).fill('weight');
        this.updateDynamicDisplayedColumns();
      }
    }
    this.updateWeightDisplayedColumns();
  }

  ngOnInit() {
    this.updateDisplayedColumns();
    this.currentSort = [
      'airlineBrandCode',
      'originAirport',
      'destinationAirport',
      'airlineProdCode',
      'commodityName',
      'aircraftTypes',
      'shcs',
      'density',
      'flightNo',
      'day',
      'uldType',
    ];
  }

  onImageError(event: Event) {
    const imgElement = event.target as HTMLImageElement;
    imgElement.src = '/assets/media/airlinesLogo/default.png';
  }

  public showSortIndicator(property: string, direction: string) {
    if (direction != this.currentSortDir) {
      return false;
    }
    const pos = this.currentSort.findIndex(
      (sortField) => sortField === property,
    );
    return pos === 0;
  }

  sortData(property: string) {
    const pos = this.currentSort.findIndex(
      (sortField) => sortField === property,
    );
    if (pos < 0) {
      this.currentSortDir = 'asc';
      this.currentSort.unshift(property);
    } else if (pos === 0) {
      if (this.currentSortDir === 'asc') {
        this.currentSortDir = 'desc';
      } else {
        this.currentSortDir = 'asc';
        this.currentSort.splice(pos, 1);
      }
    } else {
      this.currentSort.splice(pos, 1);
      this.currentSort.unshift(property);
      this.currentSortDir = 'asc';
    }
  }
  public displayAircraftTypes(type: string) {
    return displayAircraftTypes(type);
  }

  public displayRateType(type: string) {
    return displayRateType(type);
  }

  public formatMoney(rate: number, currency: string) {
    return formatMoneyToString(rate, currency);
  }

  public formatSurcharge(rate: number, currency: string) {
    if (rate === 0) {
      return 'Included';
    }
    return formatMoneyToString(rate, currency);
  }

  public renderAirlineProdCode(row: FakeData) {
    return renderAirlineProdCode(row.airlineProdCode, row.commodity);
  }

  public tipsCommodity(row: FakeData) {
    return row.airlineRateRemarks;
  }

  public tipsRfs(row: FakeData) {
    return row.rfsRemarks;
  }

  getColor(property: string, row: FakeData): any {
    if (property === 'airlineProdCode') {
      if (row.airlineProdCode) {
        return {
          color: 'blue',
        };
      }
    }
    return null;
  }

  public tipsAirlineProdCode(row: FakeData) {
    return tipsAirlineProdCode(
      row.airlineProdCode,
      row.commodity,
      row.airlineRateRemarks,
    );
  }

  rateClass(type: string): string {
    return type?.toLowerCase();
  }

  public formatDate(date: Date, format: string) {
    return this.datePipe.transform(date, format);
  }

  public formatEndDate(date: Date, format: string, tips = false) {
    if (date >= this.ufnDate) {
      return tips ? 'Until Further Notice' : 'UFN';
    } else {
      return this.formatDate(date, format);
    }
  }

  displayAirlineContact(row: FakeData): void {
    if (!row.airlineUserContactInfo || row.airlineUserContactInfo.length < 1) {
      return;
    }
    const options: NgbModalOptions = {
      windowClass: 'basic-modal',
      backdrop: true,
      keyboard: true,
      centered: true,
    };
    const modalRef = this.modalService.open(
      AirlineContactPopupComponent,
      options,
    );
    if (modalRef) {
      modalRef.componentInstance.airlineContacts = row.airlineUserContactInfo;
    }
  }

  displayGHAContact(row: FakeData): void {
    if (!row.gha) {
      return;
    }
    const options: NgbModalOptions = {
      windowClass: 'basic-modal',
      backdrop: true,
      keyboard: true,
      centered: true,
    };
    const modalRef = this.modalService.open(GhaContactPopupComponent, options);
    if (modalRef) {
      modalRef.componentInstance.row = row;
      modalRef.componentInstance.onDownload
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.downloadGhaAttachment(row);
        });
    }
  }

  displayAirlineSchedule(row: FakeData): void {
    if (
      !row?.airlineBrand?.scheduleAttachments?.length &&
      !row?.airlineBrand?.scheduleUrl
    ) {
      return;
    }
    const options: NgbModalOptions = {
      windowClass: 'basic-modal',
      backdrop: true,
      keyboard: true,
      centered: true,
    };
    const modalRef = this.modalService.open(
      AirlineSchedulePopupComponent,
      options,
    );

    modalRef.componentInstance.row = row;
    modalRef.componentInstance.onDownload
      .pipe(takeUntil(this.destroy$))
      .subscribe((attachmentId: number) => {
        this.downloadScheduleAttachment(row, attachmentId);
      });
  }

  async downloadScheduleAttachment(row: FakeData, attachmentId: number) {
    this.airlineService
      .generateDownloadUrlForScheduleAttachment(
        row.airlineBrand?.airlineCompanyId,
        attachmentId,
      )
      .then((downloadLink: PresignedURL) => {
        if (downloadLink) {
          window.open(downloadLink.url);
        }
      })
      .catch(this.handleAttachmentDownloadError);
  }

  private handleAttachmentDownloadError(error: any) {
    console.error(
      'Failed to download Schedule attachment:',
      error.message || 'an unknown error',
    );
  }

  displayAirlineGeneralInformation(row: FakeData): void {
    const options: NgbModalOptions = {
      windowClass: 'basic-modal',
      backdrop: true,
      keyboard: true,
      centered: true,
    };
    const modalRef = this.modalService.open(
      AirlineGeneralInformationPopupComponent,
      options,
    );
    modalRef.componentInstance.data = row;
  }

  async downloadGhaAttachment(row: FakeData) {
    if (
      !row.gha ||
      !row.gha.ghaAttachments?.length ||
      !row.gha.ghaAttachments[0]?.id
    ) {
      return;
    }

    this.ghaService
      .generateDownloadUrlForAttachment(row.gha.ghaAttachments[0].id, null)
      .then((downloadLink: PresignedURL) => {
        if (downloadLink) {
          window.open(downloadLink.url);
        }
      })
      .catch(this.handleDownloadError);
  }

  handleDownloadError(error: any) {
    console.error(
      'Failed to download GHA attachment:',
      error.message || 'an unknown error',
    );
  }

  public getClassForText(rate: number, row: FakeData, isAmt = false) {
    const moneyStr = formatMoneyToString(rate, row.currency);
    if (moneyStr.length > 9) {
      return isAmt ? 'amount-cell' : 'rate-cell-m';
    }
    if (this.getAllRatesAreZero(row, RateCategoryEnum[this.rateCategory])) {
      return isAmt ? 'amount-cell-blackout' : 'rate-cell-blackout';
    }
    return isAmt ? 'amount-cell' : 'rate-cell';
  }

  getAllRatesAreZero(fake: FakeData, rateCategory: RateCategoryEnum) {
    return (
      (rateCategory == RateCategoryEnum.WEIGHT
        ? WeightTypeRateList
        : ULDTypeRateList
      ).find((fieldName) => fake[fieldName] && fake[fieldName] !== 0) ===
      undefined
    );
  }

  getRateType(rate) {
    return rate.type == 'Ai' ? 'Ai-Powered' : rate.type;
  }
}
